import { convertFromRaw, EditorState } from 'draft-js';
import { cloneDeep, get, isNil, isString } from 'lodash';
import {
    AutoCellTypeObject,
    CellCoordsObj,
    CellData,
    CellTypeObject,
    HotComputedCellValue,
    TableContentData,
} from '../../../../../../common/table/TableTypes';
import { CellTypeConstants, CellTypeNames, FormatOptions } from '../../../../../../common/table/CellTypeConstants';
import { isCellTypeNumeric } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import {
    getDateFormattingOptions,
    getDateTimeValuesFromInputString,
} from '../../../../../../common/table/utils/tableInputDateTimeUtils';
import { cellCanBeRichFormatted, getDefaultTypeObject } from '../../../../../../common/table/utils/tableCellTypeUtils';
import { stringShouldFormatAsType } from '../../../../../../common/table/utils/tableInputGeneralUtils';
import rawGetText from '../../../../../../common/utils/editor/rawUtils/rawGetText';
import { isFormula } from '../formula/tableFormulaPredicateUtils';
import { getCellRenderValue } from './tableCellRenderValueUtils';

/**
 * Returns whether a cell should be treated as read only in the grid.
 */
export const cellIsReadOnly = (cellData: CellData | undefined): boolean => {
    if (!cellData || !cellData.type) return false;

    return CellTypeConstants[cellData.type.name]?.allowCellEditing === false && !isFormula(cellData.value);
};

/**
 * Collapses selected type objects down to the format values they all share.
 */
export const getSharedFormattingValuesFromCellTypes = (
    selectedCellsTypes: Array<CellTypeObject | undefined>,
): Partial<CellTypeObject> | AutoCellTypeObject | undefined => {
    if (selectedCellsTypes.length === 0) return;
    if (selectedCellsTypes.length === 1) return selectedCellsTypes[0] || { name: CellTypeNames.AUTO };

    const sharedCellFormat: { [key: string]: string | number | boolean } = {};

    const names = new Set<CellTypeNames>();
    selectedCellsTypes.forEach((typeObject) => {
        names.add(typeObject?.name || CellTypeNames.AUTO);
    });

    if (names.size === 1) {
        sharedCellFormat.name = [...names][0];
    }

    Object.values(FormatOptions).forEach((option) => {
        const values = new Set<string | number | boolean>();

        selectedCellsTypes.forEach((typeObject) => {
            if (!typeObject) return;

            const value = typeObject[option as unknown as keyof CellTypeObject];
            if (!isNil(value)) values.add(value);
        });

        if (values.size === 1) {
            sharedCellFormat[option] = [...values][0];
        }
    });

    return sharedCellFormat;
};

/**
 * Returns the shared formatting state for a persisted selection.
 */
export const getSharedFormattingValues = (
    dataArray: TableContentData,
    allSelectedCells: Array<CellCoordsObj>,
): Partial<CellTypeObject> | AutoCellTypeObject | undefined => {
    if (!allSelectedCells || allSelectedCells.length === 0) return;

    const selectedCellsTypes = allSelectedCells.map(({ row, col }) => get(dataArray, [row, col])?.type);
    return getSharedFormattingValuesFromCellTypes(selectedCellsTypes);
};

/**
 * Returns whether a type change is valid for the current cell value.
 */
export const cellTypeUpdateShouldHappen = (
    normalisedComputedCellValue: HotComputedCellValue,
    newCellType: CellTypeNames,
    locale: string,
): boolean => {
    if ([CellTypeNames.TEXT, CellTypeNames.AUTO].includes(newCellType) || isNil(normalisedComputedCellValue)) {
        return true;
    }

    const rawText = isString(normalisedComputedCellValue)
        ? normalisedComputedCellValue
        : String(normalisedComputedCellValue);

    if (newCellType === CellTypeNames.CHECKBOX) {
        return rawText?.toLowerCase() === 'true' || rawText?.toLowerCase() === 'false';
    }

    const { newCellType: inferredType } = stringShouldFormatAsType(rawText, false, locale) || {};
    if (!inferredType) return false;
    if (newCellType === inferredType) return true;

    return isCellTypeNumeric(newCellType) && isCellTypeNumeric(inferredType);
};

/**
 * Returns the updated cell model for a requested type change.
 */
export const updateCellTypeFromComputedValue = (
    prevCellData: CellData,
    cellTypeName: CellTypeNames,
    currencyPreference: string,
    locale: string,
    normalisedComputedCellValue: HotComputedCellValue,
): CellData => {
    const newCellData = cloneDeep(prevCellData);

    if (prevCellData.type?.name === cellTypeName) return newCellData;

    if (cellTypeName === CellTypeNames.AUTO) {
        newCellData.type = undefined;
        return newCellData;
    }

    if (!cellTypeUpdateShouldHappen(normalisedComputedCellValue, cellTypeName, locale)) return prevCellData;

    newCellData.type = {
        ...getDefaultTypeObject(cellTypeName),
        ...(cellTypeName === CellTypeNames.CURRENCY && { [FormatOptions.CURRENCY]: currencyPreference }),
    };

    if (cellTypeName === CellTypeNames.TEXT && prevCellData.type?.name) {
        newCellData.value = getCellRenderValue(prevCellData, locale);
    }

    if (newCellData.type.name === CellTypeNames.DATE_TIME) {
        const rawText = newCellData.draftContent ? rawGetText(newCellData.draftContent) : newCellData.value || '';
        const dateValues = getDateTimeValuesFromInputString(rawText, locale);

        if (dateValues) {
            const { hasTime, hasSeconds, dateFormat } = dateValues;
            newCellData.value = rawText;
            newCellData.type = { ...newCellData.type, hasTime, hasSeconds, dateFormat };

            const format = getDateFormattingOptions(newCellData.value, newCellData.type, {
                prevCellValue: null,
                prevTypeObject: prevCellData.type,
                locale,
            });
            newCellData.type = { ...newCellData.type, ...format };
        }
    }

    if (cellCanBeRichFormatted(prevCellData) && !cellCanBeRichFormatted(newCellData)) {
        if (newCellData.draftContent) {
            newCellData.value = rawGetText(newCellData.draftContent);
            newCellData.textStyle = EditorState.createWithContent(convertFromRaw(newCellData.draftContent))
                .getCurrentInlineStyle()
                .toArray();
        }

        delete newCellData.draftContent;
    }

    return newCellData;
};
